<!-- Global assistant dock — a right-side panel that hosts the chat from any
     view (opened via the TopBar button). Shares the chat store + turn logic
     with the full /chat page, so it's the same conversation. -->
<template>
  <transition name="dock">
    <aside
      v-if="open"
      class="fixed inset-y-0 right-0 z-40 flex w-full max-w-[420px] flex-col border-l border-line bg-surface shadow-2xl"
    >
      <header class="flex h-14 shrink-0 items-center gap-2 border-b border-line px-4">
        <span class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Sparkles class="size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-semibold leading-tight text-ink">{{ $t('chat-ui.dock.assistant') }}</p>
          <p class="truncate text-xs leading-tight text-faint">{{ store.activeSession?.title ?? $t('chat-ui.dock.newChat') }}</p>
        </div>
        <div v-if="store.activeSession?.visibility === 'shared' && peerList.length" class="mr-1 flex items-center -space-x-1.5">
          <span
            v-for="p in peerList.slice(0, 4)"
            :key="p.id"
            class="flex size-6 items-center justify-center rounded-full border-2 border-surface text-[10px] font-bold text-white"
            :style="{ backgroundColor: p.color }"
            :title="p.label ?? p.by ?? $t('chat-ui.dock.collaborator')"
          >{{ initialsOf(p.label ?? p.by) }}</span>
        </div>
        <button
          v-if="store.activeSession"
          type="button"
          class="flex size-8 items-center justify-center rounded-lg transition-colors"
          :class="store.activeSession.visibility === 'shared' ? 'bg-brand-50 text-brand-700' : 'text-muted hover:bg-gray-100 hover:text-ink'"
          :title="store.activeSession.visibility === 'shared' ? $t('chat-ui.dock.sharedTitle') : $t('chat-ui.dock.privateTitle')"
          @click="onToggleShare"
        ><Users v-if="store.activeSession.visibility === 'shared'" class="size-[18px]" /><Lock v-else class="size-[18px]" /></button>
        <button type="button" class="flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('chat-ui.dock.newChat')" @click="onNewSession"><Plus class="size-[18px]" /></button>
        <RouterLink to="/chat" class="flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('chat-ui.dock.openFullChat')" @click="closeDock"><Maximize2 class="size-[18px]" /></RouterLink>
        <button type="button" class="flex size-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-gray-100 hover:text-ink" :title="$t('common.close')" @click="closeDock"><X class="size-[18px]" /></button>
      </header>

      <div class="relative min-h-0 flex-1">
      <div ref="scrollArea" class="absolute inset-0 overflow-y-auto">
        <div v-if="openLinks.length" class="flex flex-wrap items-center gap-1.5 px-4 pt-3">
          <a
            v-for="l in openLinks"
            :key="l.id"
            :href="l.kind === 'design' ? `/design/${l.id}` : `/designer/${l.id}`"
            target="_blank"
            class="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100"
          >
            {{ l.kind === 'design' ? $t('chat-ui.dock.openDesign') : $t('chat-ui.dock.openDashboard') }} <ExternalLink class="size-3" />
          </a>
        </div>
        <MessageList :messages="store.messages" @suggest="onSend($event)" />
      </div>
        <transition name="jump">
          <button
            v-if="!atBottom"
            type="button"
            class="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink shadow-lg transition-colors hover:bg-surface-2"
            @click="scrollToBottom()"
          >
            <ChevronDown class="size-3.5" /> {{ $t('chat-ui.dock.jumpToLatest') }}
          </button>
        </transition>
      </div>

      <p v-if="typingBy" class="flex items-center gap-2 px-4 py-1.5 text-xs text-muted">
        <span class="flex gap-0.5">
          <span class="size-1.5 animate-bounce rounded-full bg-muted [animation-delay:-0.3s]"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted [animation-delay:-0.15s]"></span>
          <span class="size-1.5 animate-bounce rounded-full bg-muted"></span>
        </span>
        {{ $t('chat-ui.dock.typing', { name: typingBy }) }}
      </p>

      <p v-if="currentView" class="mx-4 mb-1 flex items-center gap-1.5 text-[11px] text-faint">
        <Eye class="size-3" /> {{ $t('chat-ui.context', { kind: currentView.kind }) }}
        <code class="truncate">{{ currentView.id }}</code>
      </p>
      <MessageInput :loading="loading" :disabled="loading" @send="onSend" @stop="cancel" @error="error = $event" />

      <p v-if="error" class="flex items-center gap-2 border-t border-danger/30 bg-danger-soft px-4 py-2 text-xs text-danger">
        <TriangleAlert class="size-3.5 shrink-0" />{{ error }}
      </p>
    </aside>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { Sparkles, Plus, Maximize2, X, ExternalLink, TriangleAlert, Users, Lock, ChevronDown, Eye } from 'lucide-vue-next';
import MessageList from './MessageList.vue';
import MessageInput from './MessageInput.vue';
import { useChatStore, type ChatAttachment } from '../../stores/chat';
import { useChatDock } from '../../composables/useChatDock';
import { useChatTurn } from '../../composables/useChatTurn';
import { useChatLive } from '../../composables/useChatLive';
import { useStickToBottom } from '../../composables/useStickToBottom';

const { open, closeDock } = useChatDock();
const store = useChatStore();
const { loading, error, openLinks, currentView, send, cancel } = useChatTurn();
const { peers, typingBy, connect: connectLive } = useChatLive();
const scrollArea = ref<HTMLElement | null>(null);
const { atBottom, scrollToBottom } = useStickToBottom(scrollArea);
const peerList = computed(() => Object.values(peers));
function initialsOf(name?: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
}

// When the dock first opens, point the store at the global scope (loads its
// sessions) and join the live collaboration room.
watch(open, (v) => {
  if (v) {
    connectLive();
    void store.setScope('global').catch((e) => { error.value = e instanceof Error ? e.message : String(e); });
  }
});

async function onNewSession(): Promise<void> {
  error.value = null;
  try { await store.newSession(); } catch (e) { error.value = e instanceof Error ? e.message : String(e); }
}

// Share the active session with the workspace (or make it private again).
async function onToggleShare(): Promise<void> {
  const s = store.activeSession;
  if (!s) return;
  error.value = null;
  try { await store.setVisibility(s.id, s.visibility === 'shared' ? 'private' : 'shared'); }
  catch (e) { error.value = e instanceof Error ? e.message : String(e); }
}
function onSend(text: string, images: ChatAttachment[] = []): void {
  void send(text, images, null);
}
</script>

<style scoped>
.dock-enter-active, .dock-leave-active { transition: transform 0.22s ease, opacity 0.22s ease; }
.dock-enter-from, .dock-leave-to { transform: translateX(100%); opacity: 0; }
.jump-enter-active, .jump-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.jump-enter-from, .jump-leave-to { opacity: 0; transform: translate(-50%, 6px); }
</style>
